import { Fragment, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useLocation } from 'react-router-dom';
import {
  Save,
  Upload,
  Download,
  Wand2,
  PenLine,
  X,
  Plus,
  MoveRight,
} from 'lucide-react';
import { useNotification } from '../hooks/useNotification';
import BookNavigationTabs from '../components/BookNavigationTabs';
import { useSyncLanguageUrl } from '../hooks/useSyncLanguageUrl';
import { getImageUrl } from '../utils/getImageUrl';

const BASE_LANGUAGE_MAP = {
  text_en: 'English',
  text_de: 'German',
  text_es: 'Spanish',
  text_nl: 'Dutch',
  text_ru: 'Russian',
  text_it: 'Italian',
  text_fr: 'French',
  text_pl: 'Polish',
  text_tr: 'Turkish',
};

const DEFAULT_COLUMNS = [
  'text_en',
  'text_de',
  'text_es',
  'text_nl',
  'text_ru',
  'text_it',
  'text_fr',
  'text_pl',
  'text_tr',
];

const COVER_ROWS = [
  { side: 'front', label: 'Front Cover' },
  { side: 'back', label: 'Back Cover' },
];

function InsertBlankPageButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      title="Insert blank page here"
      className="w-full flex items-center justify-center gap-2 py-2 text-gray-500 bg-gray-50 border border-dashed border-gray-300 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors group"
    >
      <Plus size={14} className="group-hover:scale-110 transition-transform" />
      <span className="text-xs font-medium">Insert blank page</span>
    </button>
  );
}

function BookTranslationsPage() {
  const { book_id: routeBookId, bookId } = useParams();
  const paramBookId = bookId || routeBookId;
  const location = useLocation();
  const [pages, setPages] = useState([]);
  const [additionalEntriesByPageId, setAdditionalEntriesByPageId] = useState({});
  const [coverTexts, setCoverTexts] = useState({ front: {}, back: {} });
  const [bookName, setBookName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [languageOptions, setLanguageOptions] = useState(BASE_LANGUAGE_MAP);
  const [selectedLanguages, setSelectedLanguages] = useState(DEFAULT_COLUMNS);
  const [uploadLanguage, setUploadLanguage] = useState('text_en');
  const [selectedLanguage, setSelectedLanguage] = useState('text_en');
  const [includeEnglishInExport, setIncludeEnglishInExport] = useState(false);
  const [includeCoversInExport, setIncludeCoversInExport] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [translatingField, setTranslatingField] = useState(null);
  const [pageLanguage, setPageLanguage] = useState('es_de');
  const [showEnglishEditor, setShowEnglishEditor] = useState(false);
  const [savingPageId, setSavingPageId] = useState(null);
  const [moveTargets, setMoveTargets] = useState({});
  const [movingPageId, setMovingPageId] = useState(null);
  const [showTextTransferPanel, setShowTextTransferPanel] = useState(false);
  const [showAllPages, setShowAllPages] = useState(true);
  const [showAdditionalOnly, setShowAdditionalOnly] = useState(false);
  const { notify, Notification } = useNotification();

  useSyncLanguageUrl(pageLanguage);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const langParam = searchParams.get('plang');
    if (langParam) {
      setPageLanguage(langParam);
    }
  }, [location.search]);

  const visiblePages = pages.filter((page) => (page.text_en || '').trim());
  const displayedPages = showAllPages ? pages : visiblePages;
  const displayedAdditionalPages = displayedPages.filter(
    (page) => (additionalEntriesByPageId[page._id] || []).length > 0,
  );

  const sortLanguageFields = (fields) =>
    fields.sort((a, b) => {
      const indexA = DEFAULT_COLUMNS.indexOf(a);
      const indexB = DEFAULT_COLUMNS.indexOf(b);
      if (indexA === -1 && indexB === -1) {
        return a.localeCompare(b);
      }
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

  const mapCoverResponseToState = (coverPayload) => {
    const initial = { front: {}, back: {} };
    if (coverPayload) {
      ['front', 'back'].forEach((side) => {
        (coverPayload[side] || []).forEach(({ language, text }) => {
          if (!language) return;
          initial[side][`text_${language}`] = text || '';
        });
      });
    }
    if (!initial.front.text_en) initial.front.text_en = '';
    if (!initial.back.text_en) initial.back.text_en = '';
    return initial;
  };

  const refreshLanguageState = useCallback((pagesList, coverState) => {
    const languages = new Set(DEFAULT_COLUMNS);
    pagesList.forEach((page) => {
      Object.keys(page || {}).forEach((field) => {
        if (field.startsWith('text_')) {
          languages.add(field);
        }
      });
    });
    ['front', 'back'].forEach((side) => {
      Object.keys(coverState[side] || {}).forEach((field) => {
        if (field.startsWith('text_')) {
          languages.add(field);
        }
      });
    });

    const options = { ...BASE_LANGUAGE_MAP };
    languages.forEach((field) => {
      if (!options[field]) {
        const label = field.replace('text_', '').toUpperCase();
        options[field] = label;
      }
    });

    const selected = sortLanguageFields(Array.from(languages));
    setLanguageOptions(options);
    setSelectedLanguages(selected);
  }, []);

  useEffect(() => {
    if (!paramBookId) return;

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const pagesResponse = await axios.get(`/api/list_pages/${paramBookId}`);
        let coverResponseData = { cover_texts: { front: [], back: [] } };

        try {
          const coverResponse = await axios.get(
            `/api/books/${paramBookId}/cover-texts`,
          );
          coverResponseData = coverResponse.data;
        } catch (coverError) {
          if (coverError?.response?.status !== 404) {
            throw coverError;
          }
        }

        const fetchedPages = pagesResponse.data.pages || [];
        const entriesResults = await Promise.allSettled(
          fetchedPages.map((page) =>
            axios.get(`/api/page/${page._id}/text-entries`),
          ),
        );
        const entriesByPageId = {};
        entriesResults.forEach((result, index) => {
          const pageId = fetchedPages[index]?._id;
          if (!pageId) return;
          entriesByPageId[pageId] =
            result.status === 'fulfilled'
              ? result.value.data?.page_text_entries || []
              : [];
        });
        const coverState = mapCoverResponseToState(
          coverResponseData.cover_texts,
        );

        setPages(fetchedPages);
        setAdditionalEntriesByPageId(entriesByPageId);
        setCoverTexts(coverState);
        setBookName(pagesResponse.data.book_name || '');
        refreshLanguageState(fetchedPages, coverState);
      } catch (err) {
        console.error('Failed to fetch book translations', err);
        setError('Failed to fetch pages.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [paramBookId, refreshLanguageState]);

  const handleTextChange = (pageId, field, value) => {
    setPages((prev) =>
      prev.map((page) =>
        page._id === pageId
          ? {
              ...page,
              [field]: value,
            }
          : page,
      ),
    );
  };

  const handleAdditionalTextChange = (pageId, entryId, field, value) => {
    const language = field.replace('text_', '');
    setAdditionalEntriesByPageId((prev) => ({
      ...prev,
      [pageId]: (prev[pageId] || []).map((entry) =>
        entry.id === entryId
          ? {
              ...entry,
              texts: {
                ...(entry.texts || {}),
                [language]: value,
              },
            }
          : entry,
      ),
    }));
  };

  const handleCoverTextChange = (side, field, value) => {
    setCoverTexts((prev) => ({
      ...prev,
      [side]: {
        ...(prev[side] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveText = async (pageId, field, value) => {
    try {
      const response = await axios.post('/api/update_text', {
        page_id: pageId,
        field,
        new_text: value,
      });

      if (response.status === 200) {
        notify('Text updated successfully.');
      } else {
        notify('Update failed.', 'error');
      }
    } catch (err) {
      notify('Update failed.', 'error');
    }
  };

  const handleSaveAdditionalEntries = async (pageId) => {
    const entries = additionalEntriesByPageId[pageId] || [];
    try {
      const response = await axios.put(`/api/page/${pageId}/text-entries`, {
        page_text_entries: entries,
      });
      setAdditionalEntriesByPageId((prev) => ({
        ...prev,
        [pageId]: response.data?.page_text_entries || entries,
      }));
      notify('Additional text updated successfully.');
    } catch (err) {
      console.error('Failed to save additional text entries', err);
      notify('Additional text update failed.', 'error');
    }
  };

  const handleSaveEnglishText = async (pageId, value) => {
    setSavingPageId(pageId);
    try {
      const response = await axios.post('/api/update_text', {
        page_id: pageId,
        field: 'text_en',
        new_text: value,
      });
      if (response.status === 200) {
        notify('Saved.');
      } else {
        notify('Save failed.', 'error');
      }
    } catch {
      notify('Save failed.', 'error');
    } finally {
      setSavingPageId(null);
    }
  };

  const applyMovedPages = (sourcePage, destinationPage) => {
    setPages((prev) => {
      const nextPages = prev.map((page) => {
        if (page._id === sourcePage._id) return { ...page, ...sourcePage };
        if (page._id === destinationPage._id) {
          return { ...page, ...destinationPage };
        }
        return page;
      });
      refreshLanguageState(nextPages, coverTexts);
      return nextPages;
    });
  };

  const movePageTexts = async (
    sourcePage,
    destinationPageId,
    overwrite = false,
  ) => {
    const response = await axios.post('/api/move_page_texts', {
      source_page_id: sourcePage._id,
      destination_page_id: destinationPageId,
      overwrite,
    });

    const { source_page: updatedSource, destination_page: updatedDestination } =
      response.data;
    applyMovedPages(updatedSource, updatedDestination);
    setMoveTargets((prev) => ({ ...prev, [sourcePage._id]: '' }));
    notify('Page texts moved.');
  };

  const handleMovePageTexts = async (sourcePage) => {
    const destinationPageId = moveTargets[sourcePage._id];
    if (!destinationPageId) {
      notify('Choose a destination page first.', 'error');
      return;
    }

    const destinationPage = pages.find((page) => page._id === destinationPageId);
    if (!destinationPage) {
      notify('Destination page not found.', 'error');
      return;
    }

    const textCount = Object.keys(sourcePage).filter(
      (field) =>
        field.startsWith('text_') && String(sourcePage[field] || '').trim(),
    ).length;
    if (!textCount) {
      notify('This page has no texts to move.', 'error');
      return;
    }

    const confirmed = window.confirm(
      `Move ${textCount} text field(s) from page ${sourcePage.page_number} to page ${destinationPage.page_number}?`,
    );
    if (!confirmed) return;

    setMovingPageId(sourcePage._id);
    try {
      await movePageTexts(sourcePage, destinationPageId);
    } catch (err) {
      const conflicts = err?.response?.data?.conflicting_fields || [];
      if (err?.response?.status === 409 && conflicts.length) {
        const overwrite = window.confirm(
          `Page ${destinationPage.page_number} already has text in ${conflicts.join(
            ', ',
          )}.\n\nOverwrite those destination texts?`,
        );
        if (overwrite) {
          try {
            await movePageTexts(sourcePage, destinationPageId, true);
          } catch (overwriteErr) {
            console.error('Failed to overwrite moved page texts', overwriteErr);
            notify('Failed to move page texts.', 'error');
          }
        }
      } else {
        console.error('Failed to move page texts', err);
        notify(
          err?.response?.data?.error || 'Failed to move page texts.',
          'error',
        );
      }
    } finally {
      setMovingPageId(null);
    }
  };

  const handleSaveCoverText = async (side, field) => {
    if (!paramBookId) {
      notify('Missing book identifier.', 'error');
      return;
    }

    const language = field.replace('text_', '');
    const text = coverTexts[side]?.[field] || '';

    try {
      const response = await axios.put(
        `/api/books/${paramBookId}/cover-texts/${side}/${language}`,
        { text },
      );
      const nextCoverState = mapCoverResponseToState(response.data.cover_texts);
      setCoverTexts(nextCoverState);
      refreshLanguageState(pages, nextCoverState);
      notify('Cover text updated successfully.');
    } catch (err) {
      console.error('Failed to update cover text', err);
      notify('Failed to update cover text.', 'error');
    }
  };

  const handleAutoTranslate = async (pageId, field) => {
    const targetLang = field.replace('text_', '');
    if (targetLang === 'en') {
      return;
    }

    const currentPage = pages.find((page) => page._id === pageId);
    const englishSource = currentPage?.text_en?.trim();
    if (!englishSource) {
      notify('English text is required for translation.', 'error');
      return;
    }

    try {
      setTranslatingField(`${pageId}-${field}`);
      const response = await axios.post('/api/translate_text', {
        page_id: pageId,
        target_lang: targetLang,
        text: englishSource,
      });

      if (response.status === 200) {
        const translatedText = response.data?.translated_text || '';
        setPages((prev) =>
          prev.map((page) =>
            page._id === pageId
              ? {
                  ...page,
                  [field]: translatedText,
                }
              : page,
          ),
        );
        notify(
          `Translated to ${languageOptions[field] || targetLang.toUpperCase()}.`,
        );
      } else {
        notify('Translation failed. Please try again.', 'error');
      }
    } catch (error) {
      notify('Error translating text. Check console for details.', 'error');
      console.error('Translation error', error);
    } finally {
      setTranslatingField(null);
    }
  };

  const handleAutoTranslateAdditional = async (pageId, entryId, field) => {
    const targetLang = field.replace('text_', '');
    if (targetLang === 'en') return;

    const entry = (additionalEntriesByPageId[pageId] || []).find(
      (item) => item.id === entryId,
    );
    const englishSource = entry?.texts?.en?.trim();
    if (!englishSource) {
      notify('English additional text is required for translation.', 'error');
      return;
    }

    const translationKey = `additional-${pageId}-${entryId}-${field}`;

    try {
      setTranslatingField(translationKey);
      const response = await axios.post('/api/translate_text', {
        page_id: pageId,
        target_lang: targetLang,
        text: englishSource,
      });

      if (response.status === 200) {
        handleAdditionalTextChange(
          pageId,
          entryId,
          field,
          response.data?.translated_text || '',
        );
        notify(
          `Translated additional text to ${
            languageOptions[field] || targetLang.toUpperCase()
          }.`,
        );
      } else {
        notify('Translation failed. Please try again.', 'error');
      }
    } catch (error) {
      notify('Error translating additional text.', 'error');
      console.error('Additional translation error', error);
    } finally {
      setTranslatingField(null);
    }
  };

  const handleAutoTranslateCover = async (side, field) => {
    const targetLang = field.replace('text_', '');
    if (targetLang === 'en') {
      return;
    }

    const englishSource = coverTexts[side]?.text_en?.trim();
    if (!englishSource) {
      notify('English text is required for translation.', 'error');
      return;
    }

    const translationKey = `cover-${side}-${field}`;

    try {
      setTranslatingField(translationKey);
      const response = await axios.post('/api/translate_text', {
        target_lang: targetLang,
        text: englishSource,
      });

      if (response.status === 200) {
        const translatedText = response.data?.translated_text || '';
        setCoverTexts((prev) => ({
          ...prev,
          [side]: {
            ...(prev[side] || {}),
            [field]: translatedText,
          },
        }));
        notify(
          `Translated to ${languageOptions[field] || targetLang.toUpperCase()}.`,
        );
      } else {
        notify('Translation failed. Please try again.', 'error');
      }
    } catch (error) {
      notify('Error translating text. Check console for details.', 'error');
      console.error('Cover translation error', error);
    } finally {
      setTranslatingField(null);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !paramBookId) return;

    const confirmed = window.confirm(
      `Uploading CSV file.\nLanguage chosen: ${
        languageOptions[uploadLanguage]
      }\n\nDo you want to continue?`,
    );
    if (!confirmed) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('language_field', uploadLanguage);
    formData.append('book_id', paramBookId);

    try {
      const response = await axios.post('/api/upload_texts', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        notify('Successfully uploaded and updated pages.');
        const payload = response.data;
        let updatedPages = [];
        let coverPayload = null;

        if (Array.isArray(payload)) {
          updatedPages = payload;
        } else if (payload && typeof payload === 'object') {
          updatedPages = payload.pages || [];
          coverPayload = payload.cover_texts || null;
        }

        setPages(updatedPages);

        let nextCoverState = coverTexts;
        if (coverPayload) {
          nextCoverState = mapCoverResponseToState(coverPayload);
          setCoverTexts(nextCoverState);
        }

        refreshLanguageState(updatedPages, nextCoverState);
      } else {
        notify('Failed to upload CSV file.', 'error');
      }
    } catch (err) {
      notify('Error uploading CSV.', 'error');
    }
  };

  const handleDownloadText = () => {
    if (!pages.length) {
      notify('No pages available to download.', 'error');
      return;
    }

    const escapeForCsv = (value) =>
      `"${String(value ?? '').replace(/"/g, '""')}"`;

    const csvLines = [];

    if (includeCoversInExport) {
      const appendCoverLine = (sideKey, records) => {
        const englishRaw = (records.text_en || '')
          .replace(/\r?\n|\r/g, ' ')
          .trim();
        const selectedRaw = (records[selectedLanguage] || '')
          .replace(/\r?\n|\r/g, ' ')
          .trim();

        if (selectedLanguage === 'text_en') {
          if (!englishRaw) {
            return;
          }
          csvLines.push(`${sideKey},${escapeForCsv(englishRaw)}`);
          return;
        }

        if (includeEnglishInExport) {
          if (!englishRaw && !selectedRaw) {
            return;
          }
          csvLines.push(
            [sideKey, escapeForCsv(englishRaw), escapeForCsv(selectedRaw)].join(
              ',',
            ),
          );
        } else if (selectedRaw) {
          csvLines.push(`${sideKey},${escapeForCsv(selectedRaw)}`);
        }
      };

      appendCoverLine('fc', coverTexts.front || {});
      appendCoverLine('bc', coverTexts.back || {});
    }

    pages.reduce((lines, page) => {
      const rawText = page[selectedLanguage] || '';
      const cleanText = rawText.replace(/\r?\n|\r/g, ' ').trim();
      if (!cleanText) {
        return lines;
      }

      if (selectedLanguage === 'text_en') {
        lines.push(`${page.page_number},${escapeForCsv(cleanText)}`);
        return lines;
      }

      if (includeEnglishInExport) {
        const english = (page.text_en || '').replace(/\r?\n|\r/g, ' ').trim();
        lines.push(
          [
            page.page_number,
            escapeForCsv(english),
            escapeForCsv(cleanText),
          ].join(','),
        );
      } else {
        lines.push(`${page.page_number},${escapeForCsv(cleanText)}`);
      }
      return lines;
    }, csvLines);

    if (!csvLines.length) {
      notify('No content available for the selected language.', 'error');
      return;
    }

    const textContent = csvLines.join('\n');

    const timestamp = Date.now();
    const blob = new Blob([textContent], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `texts_${paramBookId}_${timestamp}_${selectedLanguage}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleInsertBlankPage = async (afterPageNumber) => {
    try {
      await axios.post(`/api/books/${paramBookId}/insert_blank_page`, {
        insert_after_page_number: afterPageNumber,
      });
      const updated = await axios.get(`/api/list_pages/${paramBookId}`);
      const fetchedPages = updated.data.pages || [];
      setPages(fetchedPages);
      notify('Blank page inserted.');
    } catch (err) {
      console.error('Error inserting blank page:', err);
      notify('Failed to insert blank page.', 'error');
    }
  };

  return (
    <>
      <Notification />
      <div className="min-h-screen mt-10 px-6 pb-8">
        <div className="rounded-3xl bg-white shadow-sm">
        <div className="max-w-6xl mx-auto p-6">
          <BookNavigationTabs
            bookId={paramBookId}
            currentLanguage={pageLanguage}
            headerTitle={bookName || 'Book translations'}
            headerLinkTo={`/books/${paramBookId}/edit`}
            showBackButton
            showFlags={false}
          />
        </div>

        <button
          onClick={() => setShowTextTransferPanel(true)}
          className="fixed left-0 top-1/2 -translate-y-1/2 z-40 bg-gray-900 text-white flex flex-col items-center justify-center gap-2 py-4 px-2 rounded-r-lg shadow-lg hover:bg-gray-700 transition-colors"
          title="Upload/Download texts"
          style={{ writingMode: 'vertical-rl' }}
        >
          <Upload size={20} style={{ writingMode: 'horizontal-tb' }} />
          <span
            className="text-xs font-medium tracking-wide"
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
          >
            Upload/Download texts
          </span>
        </button>

        {showTextTransferPanel && (
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => setShowTextTransferPanel(false)}
          />
        )}

        <div
          className={`fixed left-0 top-0 h-full w-80 bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ${
            showTextTransferPanel ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
            <div className="flex items-center gap-2">
              <Upload size={18} className="text-gray-700" />
              <span className="font-semibold text-gray-800">
                Upload/Download texts
              </span>
            </div>
            <button
              onClick={() => setShowTextTransferPanel(false)}
              className="p-1 rounded hover:bg-gray-200"
              title="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-6">
            <section className="flex flex-col gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">
                  Upload texts
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  CSV or TXT rows are matched by page number.
                </p>
              </div>
              <label className="block text-sm font-medium text-gray-700">
                Target language
              </label>
              <select
                value={uploadLanguage}
                onChange={(event) => setUploadLanguage(event.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                {Object.keys(languageOptions).map((field) => (
                  <option key={field} value={field}>
                    {languageOptions[field]}
                  </option>
                ))}
              </select>
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800">
                <Upload size={16} />
                Choose CSV/TXT file
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleUpload}
                  className="hidden"
                />
              </label>
            </section>

            <section className="flex flex-col gap-3 border-t border-gray-200 pt-5">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">
                  Download texts
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Export the selected language for pages with content.
                </p>
              </div>
              <label className="block text-sm font-medium text-gray-700">
                Export language
              </label>
              <select
                value={selectedLanguage}
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedLanguage(value);
                  if (value === 'text_en') {
                    setIncludeEnglishInExport(false);
                  }
                }}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                {Object.keys(languageOptions).map((field) => (
                  <option key={field} value={field}>
                    {languageOptions[field]}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
                <input
                  type="checkbox"
                  checked={includeEnglishInExport}
                  onChange={(event) =>
                    setIncludeEnglishInExport(event.target.checked)
                  }
                  disabled={selectedLanguage === 'text_en'}
                  className="accent-gray-900"
                />
                Add original (EN)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
                <input
                  type="checkbox"
                  checked={includeCoversInExport}
                  onChange={(event) =>
                    setIncludeCoversInExport(event.target.checked)
                  }
                  className="accent-gray-900"
                />
                Add covers
              </label>
              <button
                onClick={handleDownloadText}
                className="flex items-center justify-center gap-2 rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
              >
                <Download size={16} />
                Download texts
              </button>
            </section>
          </div>
        </div>

        <div className="px-8 pb-6 flex flex-wrap justify-center items-center gap-4">
          <button
            onClick={() => setShowEnglishEditor(true)}
            className="flex items-center gap-2 bg-white border border-gray-300 px-4 py-2 rounded hover:bg-gray-50"
          >
            <PenLine size={18} /> Enter English Texts
          </button>
          <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
            <input
              type="checkbox"
              checked={showAllPages}
              onChange={(event) => setShowAllPages(event.target.checked)}
              disabled={showAdditionalOnly}
              className="accent-gray-900"
            />
            Show all pages
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
            <input
              type="checkbox"
              checked={showAdditionalOnly}
              onChange={(event) => setShowAdditionalOnly(event.target.checked)}
              className="accent-gray-900"
            />
            Show only additional content
          </label>
        </div>

        <div className="px-8 pb-2">
          <div className="flex justify-end mb-2">
            <select
              onChange={(e) => {
                const pageNumber = e.target.value;
                if (pageNumber) {
                  const element = document.getElementById(
                    `page-row-${pageNumber}`,
                  );
                  if (element) {
                    element.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    });
                  }
                }
              }}
              className="border border-gray-300 rounded py-1 px-3 text-sm"
            >
              <option value="">Jump to page...</option>
              {(showAdditionalOnly ? displayedAdditionalPages : displayedPages).map((page) => (
                <option key={page._id} value={page.page_number}>
                  Page {page.page_number} - {page.name}
                </option>
              ))}
            </select>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="relative overflow-visible">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
                <tr>
                  <th className="border-b border-gray-200 px-8 py-4 text-center w-64 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Page
                  </th>
                  {selectedLanguages.map((field) => (
                    <th
                      key={field}
                      className={`border-b border-gray-200 px-4 py-4 text-center align-middle text-xs font-semibold uppercase tracking-wide text-gray-500 ${
                        field === 'text_en' ? 'w-96' : ''
                      }`}
                    >
                      <span
                        className={
                          field === 'text_en' ? 'font-semibold text-gray-500' : ''
                        }
                      >
                        {languageOptions[field] ||
                          field.replace('text_', '').toUpperCase()}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {!showAdditionalOnly && COVER_ROWS.map(({ side, label }) => (
                  <tr key={`cover-${side}`} className="bg-blue-50">
                    <td className="border-r border-gray-200 px-4 py-4 align-top text-gray-700">
                      <div className="flex flex-col gap-2">
                        <span className="font-semibold text-gray-900">{label}</span>
                      </div>
                    </td>
                    {selectedLanguages.map((field) => (
                      <td
                        key={`cover-${side}-${field}`}
                        className="border-r border-gray-200 px-3 py-4 align-top overflow-visible text-gray-700 last:border-r-0"
                      >
                        <textarea
                          value={coverTexts[side]?.[field] || ''}
                          onChange={(event) =>
                            handleCoverTextChange(
                              side,
                              field,
                              event.target.value,
                            )
                          }
                          rows={6}
                          className={`border border-gray-300 rounded p-2 focus:outline-none focus:ring focus:ring-blue-200 transition-all duration-200 ${
                            field === 'text_en' ? 'text-base font-medium' : ''
                          }`}
                          style={{
                            width:
                              focusedField === `cover-${side}-${field}`
                                ? '18em'
                                : '100%',
                            minWidth: field === '12em',
                          }}
                          onFocus={() =>
                            setFocusedField(`cover-${side}-${field}`)
                          }
                          onBlur={() => setFocusedField(null)}
                        />
                        <div className="flex justify-end gap-2 mt-2">
                          <button
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleSaveCoverText(side, field);
                            }}
                            className="flex items-center gap-2 bg-white border border-gray-300 px-2 py-1 text-sm rounded hover:bg-gray-100"
                          >
                            <Save size={16} />
                            Save
                          </button>
                          {field !== 'text_en' && (
                            <button
                              onClick={() =>
                                handleAutoTranslateCover(side, field)
                              }
                              className="flex items-center gap-1 bg-emerald-500 text-white px-2 py-1 text-sm rounded hover:bg-emerald-600 disabled:opacity-60"
                              disabled={
                                translatingField === `cover-${side}-${field}` ||
                                !(coverTexts[side]?.text_en || '').trim()
                              }
                            >
                              <Wand2 size={14} />
                              {translatingField === `cover-${side}-${field}`
                                ? '...'
                                : 'Auto'}
                            </button>
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}

                {!showAdditionalOnly && pages.length > 0 && (
                  <tr>
                    <td
                      colSpan={selectedLanguages.length + 1}
                      className="bg-white px-4 py-3"
                    >
                      <InsertBlankPageButton
                        onClick={() => handleInsertBlankPage(pages[0].page_number - 1)}
                      />
                    </td>
                  </tr>
                )}

                {(showAdditionalOnly ? displayedAdditionalPages : displayedPages).map((page, rowIndex) => (
                  <Fragment key={page._id}>
                    {!showAdditionalOnly && (
                    <tr
                      id={`page-row-${page.page_number}`}
                      className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                    >
                      <td className="border-r border-gray-200 px-4 py-4 align-top text-gray-700">
                        <div className="flex flex-col gap-2">
                          <span className="font-semibold text-gray-900">
                            {page.page_number}. {page.name}
                          </span>
                          <div className="flex flex-col gap-1 pt-2">
                            <label className="text-xs font-medium text-gray-500">
                              Move texts to
                            </label>
                            <div className="flex items-center gap-2">
                              <select
                                value={moveTargets[page._id] || ''}
                                onChange={(event) =>
                                  setMoveTargets((prev) => ({
                                    ...prev,
                                    [page._id]: event.target.value,
                                  }))
                                }
                                className="min-w-0 flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
                              >
                                <option value="">Page...</option>
                                {pages
                                  .filter(
                                    (targetPage) => targetPage._id !== page._id,
                                  )
                                  .map((targetPage) => (
                                    <option
                                      key={targetPage._id}
                                      value={targetPage._id}
                                    >
                                      {targetPage.page_number}. {targetPage.name}
                                    </option>
                                  ))}
                              </select>
                              <button
                                onClick={() => handleMovePageTexts(page)}
                                disabled={
                                  movingPageId === page._id ||
                                  !moveTargets[page._id]
                                }
                                title="Move all texts from this page"
                                className="shrink-0 flex items-center justify-center bg-white border border-gray-300 text-gray-700 p-1.5 rounded hover:bg-gray-100 disabled:opacity-50"
                              >
                                <MoveRight size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                      {selectedLanguages.map((field) => (
                        <td
                          key={`${page._id}-${field}`}
                          className="border-r border-gray-200 px-3 py-4 align-top overflow-visible text-gray-700 last:border-r-0"
                        >
                          <textarea
                            value={page[field] || ''}
                            onChange={(event) =>
                              handleTextChange(
                                page._id,
                                field,
                                event.target.value,
                              )
                            }
                            rows={6}
                            className={`border border-gray-300 rounded p-2 focus:outline-none focus:ring focus:ring-blue-200 transition-all duration-200 ${
                              field === 'text_en' ? 'text-base font-medium' : ''
                            }`}
                            style={{
                              width:
                                focusedField === `${page._id}-${field}`
                                  ? '18em'
                                  : '100%',
                              minWidth: field === 'text_en' ? '20em' : '12em',
                            }}
                            onFocus={() =>
                              setFocusedField(`${page._id}-${field}`)
                            }
                            onBlur={() => setFocusedField(null)}
                          />
                          <div className="flex justify-end gap-2 mt-2">
                            <button
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleSaveText(
                                  page._id,
                                  field,
                                  page[field] || '',
                                );
                              }}
                              className="flex items-center gap-2 bg-white border border-gray-300 px-2 py-1 text-sm rounded hover:bg-gray-100"
                            >
                              <Save size={16} />
                              Save
                            </button>
                            {field !== 'text_en' && (
                              <button
                                onClick={() =>
                                  handleAutoTranslate(page._id, field)
                                }
                                className="flex items-center gap-1 bg-emerald-500 text-white px-2 py-1 text-sm rounded hover:bg-emerald-600 disabled:opacity-60"
                                disabled={
                                  translatingField === `${page._id}-${field}`
                                }
                              >
                                <Wand2 size={14} />
                                {translatingField === `${page._id}-${field}`
                                  ? '...'
                                  : 'Auto'}
                              </button>
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                    )}
                    {(additionalEntriesByPageId[page._id] || []).map((entry, entryIndex) => (
                      <tr
                        key={`${page._id}-${entry.id}`}
                        id={showAdditionalOnly && entryIndex === 0 ? `page-row-${page.page_number}` : undefined}
                        className="bg-amber-50/60"
                      >
                        <td className="border-r border-amber-200 px-4 py-4 align-top text-gray-700">
                          <div className="flex flex-col gap-2">
                            <span className="font-semibold text-gray-900">
                              {page.page_number}. {page.name}
                            </span>
                            <span className="inline-flex w-fit rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                              {entry.label || `Additional ${entryIndex + 1}`}
                            </span>
                          </div>
                        </td>
                        {selectedLanguages.map((field) => {
                          const language = field.replace('text_', '');
                          const value = entry.texts?.[language] || '';
                          const translationKey = `additional-${page._id}-${entry.id}-${field}`;

                          return (
                            <td
                              key={`${page._id}-${entry.id}-${field}`}
                              className="border-r border-amber-200 px-3 py-4 align-top text-gray-700 last:border-r-0"
                            >
                              <textarea
                                value={value}
                                onChange={(event) =>
                                  handleAdditionalTextChange(
                                    page._id,
                                    entry.id,
                                    field,
                                    event.target.value,
                                  )
                                }
                                rows={6}
                                className={`w-full rounded border border-amber-200 bg-white p-2 focus:outline-none focus:ring focus:ring-amber-200 ${
                                  field === 'text_en' ? 'text-base font-medium' : ''
                                }`}
                                style={{
                                  minWidth: field === 'text_en' ? '20em' : '12em',
                                }}
                              />
                              <div className="mt-2 flex justify-end gap-2">
                                <button
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSaveAdditionalEntries(page._id);
                                  }}
                                  className="flex items-center gap-2 rounded border border-gray-300 bg-white px-2 py-1 text-sm hover:bg-gray-100"
                                >
                                  <Save size={16} />
                                  Save
                                </button>
                                {field !== 'text_en' && (
                                  <button
                                    onClick={() =>
                                      handleAutoTranslateAdditional(
                                        page._id,
                                        entry.id,
                                        field,
                                      )
                                    }
                                    className="flex items-center gap-1 rounded bg-emerald-500 px-2 py-1 text-sm text-white hover:bg-emerald-600 disabled:opacity-60"
                                    disabled={
                                      translatingField === translationKey ||
                                      !(entry.texts?.en || '').trim()
                                    }
                                  >
                                    <Wand2 size={14} />
                                    {translatingField === translationKey ? '...' : 'Auto'}
                                  </button>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {!showAdditionalOnly && (
                    <tr>
                      <td
                        colSpan={selectedLanguages.length + 1}
                        className="bg-white px-4 py-3"
                      >
                        <InsertBlankPageButton
                          onClick={() => handleInsertBlankPage(page.page_number)}
                        />
                      </td>
                    </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </div>

        {loading && (
            <p className="text-center text-gray-600 mt-6">Loading...</p>
          )}
          {error && <p className="text-center text-red-500 mt-6">{error}</p>}
          {!loading && !pages.length && !error && (
            <p className="text-center text-gray-500 mt-6">
              No pages found for this book.
            </p>
          )}
        </div>
        </div>

      {/* English Text Entry Modal */}
      {showEnglishEditor && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-gray-950/90">
          {/* Modal header */}
          <div className="flex items-center justify-between px-8 py-4 bg-white border-b border-gray-200 shadow-sm shrink-0">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Enter English Texts</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                View each page and type or paste the English text. Save individually.
              </p>
            </div>
            <button
              onClick={() => setShowEnglishEditor(false)}
              className="flex items-center gap-2 px-4 py-2 rounded hover:bg-gray-100 text-gray-600"
            >
              <X size={20} />
              <span className="text-sm font-medium">Close</span>
            </button>
          </div>

          {/* Scrollable page list */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <div className="max-w-5xl mx-auto flex flex-col gap-2">
              {pages.length > 0 && (
                <InsertBlankPageButton
                  onClick={() => handleInsertBlankPage(pages[0].page_number - 1)}
                />
              )}
              {pages.map((page) => (
                <div key={page._id} className="flex flex-col gap-2">
                  <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    {/* Page header bar */}
                    <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
                      <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                        Page {page.page_number}
                      </span>
                      {page.name && (
                        <span className="text-sm text-gray-400">— {page.name}</span>
                      )}
                      {(page.text_en || '').trim() === '' && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                          No English text yet
                        </span>
                      )}
                    </div>

                    {/* Two-column layout: image | textarea */}
                    <div className="flex gap-0 min-h-[420px]">
                      {/* Image side */}
                      <div className="w-1/2 bg-gray-100 flex items-center justify-center p-4 border-r border-gray-200">
                        {page['image_preview_' + pageLanguage] ? (
                          <img
                            src={getImageUrl(page['image_preview_' + pageLanguage])}
                            alt={`Page ${page.page_number}`}
                            className="max-h-[500px] max-w-full object-contain rounded shadow"
                          />
                        ) : page.image_path ? (
                          <img
                            src={`/uploads/${page.image_path}`}
                            alt={`Page ${page.page_number}`}
                            className="max-h-[500px] max-w-full object-contain rounded shadow"
                          />
                        ) : (
                          <div className="flex items-center justify-center text-gray-400 text-sm">
                            No image available
                          </div>
                        )}
                      </div>

                      {/* Text side */}
                      <div className="w-1/2 flex flex-col p-6 gap-4">
                        <label className="text-sm font-semibold text-gray-700">
                          English text
                        </label>
                        <textarea
                          value={page.text_en || ''}
                          onChange={(e) =>
                            handleTextChange(page._id, 'text_en', e.target.value)
                          }
                          placeholder="Type or paste the English text for this page…"
                          className="flex-1 border border-gray-300 rounded-lg p-4 text-base leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-gray-900/20 transition-all"
                          rows={12}
                        />
                        <div className="flex justify-end">
                          <button
                            onClick={() =>
                              handleSaveEnglishText(page._id, page.text_en || '')
                            }
                            disabled={savingPageId === page._id}
                            className="flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 rounded-lg hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium"
                          >
                            <Save size={16} />
                            {savingPageId === page._id ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <InsertBlankPageButton
                    onClick={() => handleInsertBlankPage(page.page_number)}
                  />
                </div>
              ))}

              {pages.length === 0 && (
                <div className="text-center text-gray-400 py-20">
                  No pages loaded yet.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default BookTranslationsPage;
